import { Builder } from '@builder.io/react';

import ProminentSearch, {
  defaultContentTypes,
} from '../../../../app/Search/components/ProminentSearch/ProminentSearch';
import { SearchFilterContentTypes } from '../../../../app/Shared/enums';
import { ButtonProps } from '../../../storyblocks-design-system/components/atoms/Button/types';

const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'outlined-dark',
  'outlined-light',
  'text',
  'alternate',
];

const REL_VALUES = [
  'alternate',
  'author',
  'bookmark',
  'external',
  'help',
  'license',
  'next',
  'prev',
  'search',
  'tag',
  'nofollow',
  'noopener',
  'noreferrer',
  'dns-prefetch',
  'preconnect',
  'preload',
  'prefetch',
  'prerender',
  'archives',
  'stylesheet',
  'pingback',
];

export type ProminentSearchButtons = ButtonProps & {
  text: string;
  href?: string;
  showCallToAction: boolean;
  mobileOnly?: boolean;
};

export const config = {
  name: 'Prominent Search',
  inputs: [
    {
      name: 'headingFirstLine',
      type: 'string',
      required: false,
      defaultValue: 'Lorem ipsum dolor sit amet',
    },
    {
      name: 'headingSecondLine',
      type: 'string',
      required: false,
      defaultValue: '',
    },
    {
      name: 'trendingTags',
      type: 'string',
      required: false,
      defaultValue: '',
      helperText: 'Comma-separated list of trending tags',
    },
    {
      name: 'videoSrc',
      type: 'file',
      allowedFileTypes: ['webm', 'mp4'],
      required: false,
      defaultValue: '',
      helperText: 'Path of background video file',
    },
    {
      name: 'posterSrc',
      type: 'file',
      allowedFileTypes: ['jpg', 'png'],
      required: false,
      defaultValue: '',
      helperText: 'Path of background poster file',
    },
    {
      name: 'imageSrc',
      type: 'file',
      allowedFileTypes: ['jpg', 'png'],
      required: false,
      defaultValue: '/home/video-homepage-hero-background.jpg',
      helperText:
        'This image will be displayed if there is no background video uploaded',
    },
    {
      name: 'contentTypes',
      type: 'list',
      required: false,
      helperText: 'List of clickable content types / options',
      defaultValue: defaultContentTypes,
      subFields: [
        {
          name: 'id',
          type: 'string',
          required: true,
          enum: Object.values(SearchFilterContentTypes),
          defaultValue: SearchFilterContentTypes.All_videos_content_type,
          helperText: 'URL root',
        },
        {
          name: 'label',
          type: 'string',
          required: true,
          defaultValue: '[Label]',
          helperText: 'Text label for content type',
        },
        {
          name: 'placeholderText',
          type: 'string',
          required: true,
          defaultValue: 'Search [CONTENT] library...',
          helperText: 'Search box placeholder text',
        },
      ],
    },
    {
      name: 'socialProof',
      type: 'list',
      helperText: 'Add logos',
      required: false,
      subFields: [
        {
          name: 'logoAlt',
          type: 'string',
        },
        {
          name: 'logoImage',
          type: 'file',
          allowedFileTypes: ['jpg', 'svg'],
          helperText: 'upload image file (jpg or svg)',
        },
      ],
      onChange: (options) => {
        if (options.get('socialProof').length > 8) {
          options.set('socialProof', options.get('socialProof').slice(0, 8));
          alert(
            'Maximum number of logos is 8. Modify an existing logo instead.'
          );
        }
      },
    },
    {
      name: 'showCallToAction',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'callToAction',
      type: 'object',
      defaultValue: {
        text: 'Explore',
        href: '/',
        variant: BUTTON_VARIANTS[0],
        size: 'default',
        target: '_self',
        rel: '',
        mobileOnly: true,
      },
      showIf: (options) => options.get('showCallToAction'),
      subFields: [
        {
          name: 'text',
          type: 'string',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          required: true,
        },
        {
          name: 'target',
          type: 'string',
          enum: ['_self', '_blank', '_parent', '_top'],
          helperText:
            'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
        },
        {
          name: 'rel',
          type: 'string',
          enum: REL_VALUES,
          helperText:
            'For documentation on accepted values, see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types for link types allowed in <a> elements',
        },
        {
          name: 'variant',
          type: 'string',
          enum: BUTTON_VARIANTS,
        },
        {
          name: 'size',
          type: 'string',
          enum: ['default', 'large'],
        },
        {
          name: 'disabled',
          type: 'boolean',
          defaultValue: false,
          enum: [
            {
              label: 'Enabled',
              value: false,
            },
            {
              label: 'Disabled',
              value: true,
            },
          ],
        },
        {
          name: 'mobileOnly',
          type: 'boolean',
          defaultValue: true,
          enum: [
            {
              label: 'Enabled',
              value: true,
            },
            {
              label: 'Disabled',
              value: false,
            },
          ],
        },
      ],
    },
    {
      name: 'showSecondaryCta',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'secondaryCta',
      type: 'object',
      defaultValue: {
        text: 'Secondary CTA',
        href: '/',
        variant: BUTTON_VARIANTS[3],
        size: 'default',
        target: '_self',
        rel: '',
      },
      showIf: (options) => options.get('showSecondaryCta'),
      subFields: [
        {
          name: 'text',
          type: 'string',
          default: '',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          default: '/',
          required: true,
        },
        {
          name: 'variant',
          type: 'string',
          default: BUTTON_VARIANTS[1],
          enum: BUTTON_VARIANTS,
        },
        {
          name: 'size',
          type: 'string',
          default: 'default',
          enum: ['default', 'large'],
        },
        {
          name: 'disabled',
          type: 'boolean',
          defaultValue: false,
          enum: [
            {
              label: 'Enabled',
              value: false,
            },
            {
              label: 'Disabled',
              value: true,
            },
          ],
        },
        {
          name: 'target',
          type: 'string',
          enum: ['_self', '_blank', '_parent', '_top'],
          helperText:
            'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
        },
        {
          name: 'rel',
          type: 'string',
          enum: REL_VALUES,
          helperText:
            'For documentation on accepted values, see https://developer.mozilla.org/en-US/docs/Web/HTML/Link_types for link types allowed in <a> elements',
        },
        {
          name: 'mobileOnly',
          type: 'boolean',
          defaultValue: true,
          enum: [
            {
              label: 'Enabled',
              value: false,
            },
            {
              label: 'Disabled',
              value: true,
            },
          ],
        },
      ],
    },
    {
      name: 'hideSearchBarInMobile',
      type: 'boolean',
      defaultValue: true,
      helperText: 'Hide the search bar on mobile devices.',
    },
    {
      name: 'extraPaddingBottom',
      type: 'boolean',
      defaultValue: false,
      helperText: 'This adds an extra 96px to the bottom of the component.',
    },
    {
      name: 'subheaderDynamicContent',
      type: 'uiBlocks',
      defaultValue: {
        blocks: [],
      },
    },
    {
      name: 'dynamicContent',
      type: 'uiBlocks',
      defaultValue: {
        blocks: [],
      },
    },
  ],
};

Builder.registerComponent(ProminentSearch, config);
